import React, { useEffect, useState } from 'react';
import cx from 'classnames';

import { Button, Input, IconContact1, IconLock } from '@trqk/components';
import { locale } from '@trqk/utils';
import { useGetTrqkUserCompanyLazyQuery } from '@trqk/api';

import { useModal } from '../../../../hooks';
import { ChangePasswordModal } from './ChangePasswordModal';

const inputClasses = 'my-3 border border-tertiary-200';
const labelClasses = 'font-semibold text-sm';

/**
 * @typedef {ProfileProps}
 * @property {User} currentUser the currently logged in user
 */
interface ProfileProps {
    currentUser: User;
}

/**
 * A section of the Account page that allows the user to change their name, company, email or
 * password.
 * @param props {ProfileProps}
 */
export const Profile = ({ currentUser }: ProfileProps): JSX.Element => {
    const modal = useModal();
    // TODO determine how to handle API calls to update these user fields
    // and enable Inputs below
    // https://app.asana.com/0/1161035310203426/1190084540645105
    const [name, setName] = useState(currentUser?.name ?? '');
    const [email, setEmail] = useState(currentUser?.username ?? '');
    const userId = currentUser?.id ?? null;

    const [getTrqkUser, getTrqkUserQuery] = useGetTrqkUserCompanyLazyQuery();

    useEffect(() => {
        if (userId !== null) {
            getTrqkUser({
                variables: {
                    id: userId,
                },
            });
        }
    }, [userId]);

    const company = getTrqkUserQuery?.data?.getTrqkUser?.company ?? '';

    return (
        <div className="px-12 py-10 bg-white rounded-lg">
            <div className="flex items-center">
                <IconContact1 fillColor="primary-500" width={35} />
                <h2 className="text-xl ml-2" data-cy="profile-section-title">
                    {locale.enUS.ACCOUNT.SECTIONS.PROFILE.TITLE}
                </h2>
            </div>
            <div className="w-4/5 flex mt-8">
                <div className="w-1/2 mr-3">
                    <div className={labelClasses}>
                        {locale.enUS.ACCOUNT.SECTIONS.PROFILE.INPUTS.NAME}
                    </div>
                    <Input
                        addClasses={inputClasses}
                        inputId="name"
                        icon="name"
                        placeholder={
                            locale.enUS.ACCOUNT.SECTIONS.PROFILE.INPUTS.NAME
                        }
                        value={name}
                        handleChange={setName}
                        disabled={true}
                    />
                    <div className={cx('mt-6', labelClasses)}>
                        {locale.enUS.ACCOUNT.SECTIONS.PROFILE.INPUTS.EMAIL}
                    </div>
                    <Input
                        addClasses={inputClasses}
                        inputId="username"
                        icon="email"
                        type="email"
                        value={email}
                        placeholder={
                            locale.enUS.ACCOUNT.SECTIONS.PROFILE.INPUTS.EMAIL
                        }
                        handleChange={setEmail}
                        disabled={true}
                    />
                </div>
                <div className="w-1/2 ml-3">
                    <div className={labelClasses}>
                        {locale.enUS.ACCOUNT.SECTIONS.PROFILE.INPUTS.COMPANY}
                    </div>
                    <Input
                        addClasses={inputClasses}
                        inputId="company"
                        icon="name"
                        placeholder={
                            locale.enUS.ACCOUNT.SECTIONS.PROFILE.INPUTS.COMPANY
                        }
                        value={company}
                        disabled={true}
                    />
                    <div className={cx('mt-6', labelClasses)}>
                        {locale.enUS.ACCOUNT.SECTIONS.PROFILE.INPUTS.PASSWORD}
                    </div>
                    <Button
                        addClasses="my-3"
                        color="tertiary-light-bg"
                        autoWidth={true}
                        handleClick={(): void => {
                            modal.add({
                                Content: ChangePasswordModal,
                                input: {
                                    currentUser,
                                },
                            });
                        }}
                    >
                        <div className="flex items-center justify-center mx-12">
                            <IconLock
                                fillColor="primary-500"
                                addClasses="mr-2"
                            />
                            <div>
                                {
                                    locale.enUS.ACCOUNT.SECTIONS.PROFILE.BUTTONS
                                        .CHANGE_PASSWORD
                                }
                            </div>
                        </div>
                    </Button>
                </div>
            </div>
        </div>
    );
};
